import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PhoneInput from 'react-phone-number-input';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { useToast } from '@/hooks/useToast';
import { CustomerApi } from '@/api';
import { Plus, Edit, Trash2 } from 'lucide-react';

const GSTIN_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/;

export default function Customers() {
  const { push } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 25;
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    designation: '',
    email: '',
    phone: '',
    whatsapp: '',
    billing_address: '',
    shipping_address: '',
    state: '',
    state_code: '',
    gstin: '',
    country: 'India',
    customer_type: 'domestic' as 'domestic' | 'export',
    currency_default: 'INR' as 'INR' | 'USD' | 'EUR',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 whenever search changes
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const customersQuery = useQuery({
    queryKey: ['customers', debouncedSearch, page],
    queryFn: () => CustomerApi.list({ ...(debouncedSearch && { q: debouncedSearch }), page, limit: LIMIT }),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => CustomerApi.create(data),
    onSuccess: () => {
      push({ tone: 'success', title: 'Customer created' });
      setModalOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to create customer', body: err.message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => CustomerApi.update(editId!, data),
    onSuccess: () => {
      push({ tone: 'success', title: 'Customer updated' });
      setModalOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to update customer', body: err.message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => CustomerApi.remove(id),
    onSuccess: () => {
      push({ tone: 'success', title: 'Customer deleted' });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to delete customer', body: err.message });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      contact_person: '',
      designation: '',
      email: '',
      phone: '',
      whatsapp: '',
      billing_address: '',
      shipping_address: '',
      state: '',
      state_code: '',
      gstin: '',
      country: 'India',
      customer_type: 'domestic',
      currency_default: 'INR',
    });
    setEditId(null);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.billing_address) newErrors.billing_address = 'Billing address is required';
    if (formData.customer_type === 'domestic' && !formData.gstin) {
      newErrors.gstin = 'GSTIN is required for domestic customers';
    }
    if (formData.gstin && !GSTIN_REGEX.test(formData.gstin)) {
      newErrors.gstin = 'Invalid GSTIN format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenModal = (customer?: any) => {
    if (customer) {
      setFormData({ ...customer, phone: customer.phone ?? '', whatsapp: customer.whatsapp ?? '' });
      setEditId(customer.id);
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleSave = () => {
    if (!validateForm()) return;
    const data = {
      ...formData,
      gstin: formData.gstin || null,
      contact_person: formData.contact_person || null,
      designation: formData.designation || null,
      email: formData.email || null,
      phone: formData.phone || null,
      whatsapp: formData.whatsapp || null,
      shipping_address: formData.shipping_address || null,
      state: formData.state || null,
      state_code: formData.state_code || null,
    };

    if (editId) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const customersPage = customersQuery.data || { data: [], total: 0, page: 1, limit: LIMIT };
  const filtered = customersPage.data;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Customers"
        subtitle="Manage your customer list"
        actions={
          <button
            className="btn btn-primary gap-2"
            onClick={() => handleOpenModal()}
          >
            <Plus size={16} /> Add Customer
          </button>
        }
      />

      {/* Filters */}
      <div className="card card-pad flex flex-wrap gap-4 items-end">
        <div className="w-full sm:flex-1 sm:min-w-56">
          <label className="label">Search</label>
          <input
            type="text"
            className="input mt-1"
            placeholder="Name, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      {customersQuery.isLoading ? (
        <div className="text-center py-12 text-muted">Loading…</div>
      ) : filtered.length === 0 && customersPage.total === 0 ? (
        <EmptyState
          title="No customers found"
          body="Add your first customer to get started."
          action={
            <button
              className="btn btn-primary"
              onClick={() => handleOpenModal()}
            >
              Add Customer
            </button>
          }
        />
      ) : (
        <div className="card flex flex-col md:max-h-[calc(100vh-220px)]">
          <div className="overflow-auto flex-1">
            <table className="table-clean">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>Name</th>
                  <th>Contact Person</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Type</th>
                  <th>GSTIN</th>
                  <th>Currency</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c: any) => (
                  <tr key={c.id}>
                    <td className="font-medium">{c.name}</td>
                    <td className="text-sm text-muted">{c.contact_person || '-'}</td>
                    <td className="text-sm">{c.email || '-'}</td>
                    <td className="text-sm text-muted">{c.phone || '-'}</td>
                    <td className="text-sm capitalize">{c.customer_type}</td>
                    <td className="text-sm font-mono text-muted">{c.gstin || '-'}</td>
                    <td className="text-sm">{c.currency_default}</td>
                    <td className="flex gap-1">
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => handleOpenModal(c)}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => deleteMutation.mutate(c.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-line px-4 shrink-0">
            <Pagination
              page={customersPage.page}
              total={customersPage.total}
              limit={customersPage.limit}
              onChange={setPage}
            />
          </div>
        </div>
      )}

      {/* Modal */}
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          resetForm();
        }}
        title={editId ? 'Edit Customer' : 'Add Customer'}
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              Save
            </button>
          </>
        }
      >
        <div className="space-y-3 max-h-96 overflow-y-auto">
          <div>
            <label className="label">Name *</label>
            <input
              type="text"
              className={`input mt-1 ${errors.name ? 'input-error' : ''}`}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            {errors.name && <div className="text-xs text-rdx mt-1">{errors.name}</div>}
          </div>

          <div>
            <label className="label">Contact Person</label>
            <input
              type="text"
              className="input mt-1"
              value={formData.contact_person}
              onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
            />
          </div>

          <div>
            <label className="label">Designation</label>
            <input
              type="text"
              className="input mt-1"
              value={formData.designation}
              onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
            />
          </div>

          <div>
            <label className="label">Email</label>
            <input
              type="email"
              className="input mt-1"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Phone</label>
              <PhoneInput
                international
                defaultCountry="IN"
                value={formData.phone}
                onChange={(v) => setFormData({ ...formData, phone: v ?? '' })}
                className="phone-input mt-1"
              />
            </div>
            <div>
              <label className="label">WhatsApp</label>
              <PhoneInput
                international
                defaultCountry="IN"
                value={formData.whatsapp}
                onChange={(v) => setFormData({ ...formData, whatsapp: v ?? '' })}
                className="phone-input mt-1"
              />
            </div>
          </div>

          <div>
            <label className="label">Billing Address *</label>
            <textarea
              className={`input mt-1 ${errors.billing_address ? 'input-error' : ''}`}
              value={formData.billing_address}
              onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
              rows={2}
            />
            {errors.billing_address && <div className="text-xs text-rdx mt-1">{errors.billing_address}</div>}
          </div>

          <div>
            <label className="label">Shipping Address</label>
            <textarea
              className="input mt-1"
              value={formData.shipping_address}
              onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">State</label>
              <input
                type="text"
                className="input mt-1"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              />
            </div>
            <div>
              <label className="label">State Code</label>
              <input
                type="text"
                className="input mt-1"
                value={formData.state_code}
                onChange={(e) => setFormData({ ...formData, state_code: e.target.value })}
                placeholder="e.g. 27"
              />
            </div>
          </div>

          <div>
            <label className="label">Country</label>
            <input
              type="text"
              className="input mt-1"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            />
          </div>

          <div>
            <label className="label">Customer Type</label>
            <select
              className="input mt-1"
              value={formData.customer_type}
              onChange={(e) => setFormData({ ...formData, customer_type: e.target.value as any })}
            >
              <option value="domestic">Domestic</option>
              <option value="export">Export</option>
            </select>
          </div>

          <div>
            <label className="label">GSTIN {formData.customer_type === 'domestic' ? '*' : ''}</label>
            <input
              type="text"
              className={`input mt-1 ${errors.gstin ? 'input-error' : ''}`}
              value={formData.gstin}
              onChange={(e) => setFormData({ ...formData, gstin: e.target.value.toUpperCase() })}
              placeholder="e.g. 27AAFCU5055K1Z0"
            />
            {errors.gstin && <div className="text-xs text-rdx mt-1">{errors.gstin}</div>}
            {!errors.gstin && formData.gstin && (
              <div className="text-xs text-emerald-600 mt-1">Valid GSTIN</div>
            )}
          </div>

          <div>
            <label className="label">Default Currency</label>
            <select
              className="input mt-1"
              value={formData.currency_default}
              onChange={(e) => setFormData({ ...formData, currency_default: e.target.value as any })}
            >
              <option value="INR">INR</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
}
